/**
 * This source code is quoted from @tinper/rc-tree-select.
 * homepage: https://github.com/react-component/tree-select
 */
import React, {useState, useRef, useCallback, useEffect, useMemo} from 'react';
import Resizable, {ResizeCallback} from 're-resizable';
import classNames from 'classnames';
// import PropTypes from 'prop-types';
import RcTreeSelect, {SHOW_ALL, SHOW_CHILD, SHOW_PARENT, TreeNode} from '@tinper/rc-tree-select';
import warning from 'warning';
import {getLangInfo} from "../../wui-locale/src/tool";
import i18n from './i18n';
import {cssUtil, Warning, WebUI, setComponentClass, setComponentSize, getNid} from "../../wui-core/src/index"
import {WithConfigConsumer} from "../../wui-provider/src/context";
import Icon from '../../wui-icon/src';
import Divider from '../../wui-divider/src';
import { TreeSelectProps, TreeSelectRef, TreeDataOption, FieldNames } from './iTreeSelect'

const defaultProps = {
    // transitionName: 'slide-up',
    // choiceTransitionName: 'zoom',
    size: 'md',
    showSearch: false,
    // notFoundContent: "无匹配结果",
    locale: "zh-cn",
    onSearch: () => {},
    fieldid: '',
    showCheckedStrategy: SHOW_ALL,
    defaultOpen: false,
    showArrow: true,
    readOnly: false
};
const {isShouldUpdate} = Warning;

interface EnableProps {
    bottom?: boolean,
    top?: boolean,
    left?: boolean,
    right?: boolean,
    bottomLeft?: boolean,
    topLeft?: boolean,
    topRight?: boolean,
    bottomRight?: boolean
}

const TreeSelect: React.FC<TreeSelectProps> = (props) => {
    const [innerListHeight, setInnerListHeight] = useState(props.listHeight || 200);
    const [minWidth, setMinWidth] = useState(0);
    const [maxHeight, setMaxHeight] = useState(window.innerHeight);
    const [maxWidth, setMaxWidth] = useState(window.innerWidth);
    const [resizeClass, setResizeClass] = useState<string | undefined>(undefined);
    const [enable, setEnable] = useState<EnableProps | undefined>(undefined);
    const [open, setOpen] = useState(props.open ?? props.defaultOpen);

    const footerHeightRef = useRef<number>(0);
    const footerRef = useRef<HTMLElement | null>(null);
    const rcTreeSelectRef = useRef<TreeSelectRef | null>(null);
    const addTreeNodesFieldIdRef = useRef<boolean>(false);

    useEffect(() => {
        warning(
            props.multiple !== false || !props.treeCheckable,
            'TreeSelect',
            '`multiple` will alway be `true` when `treeCheckable` is true',
        );
    }, [props.multiple, props.treeCheckable]);

    useEffect(() => {
        setOpen(props.open ?? props.defaultOpen);
    }, [props.open]);

    // loopTreeData = (data) => { // 处理传treeData的情况
    //     let res = []
    //     for (let i = 0; i < data.length; i++) {
    //         data[i] = data[i] || {}
    //         res = res.concat(data[i].value)
    //         if (data[i].children) {
    //             res = res.concat(this.loopTreeData(data[i].children))
    //         }
    //     }
    //     return res
    // }

    // loopChildren = children => { // 处理传children的情况
    //     if (!Array.isArray(children)) {
    //         children = [children]
    //     }
    //     let res = []
    //     for (let i = 0; i < children.length; i++) {
    //         const value = children[i]?.props?.value
    //         if (value) {
    //             res.push(value)
    //         }
    //         const childrenArr = children[i]?.props?.children
    //         if (childrenArr) {
    //             res = res.concat(this.loopChildren(childrenArr))
    //         }
    //     }
    //     return res
    // }

    // modifyValues(props) {
    //     const { treeData, children } = props
    //     return treeData ? this.loopTreeData(treeData) : this.loopChildren(children)
    // }

    const saveTreeSelect = useCallback((node: TreeSelectRef) => {
        rcTreeSelectRef.current = node;
    }, []);

    const hasChildren = useMemo(() => {
        const {children, treeData, loadData} = props;
        if (loadData) return false // 如果是动态加载对树选择组件，默认有子组件
        if (children) {
            let res = true
            React.Children.forEach(children, (child: React.ReactElement) => {
                if (child.props.children && child.props.children.length) {
                    res = false
                }
            })
            return res
        }
        if (treeData && treeData.length) {
            let key = 'children'
            const {fieldNames} = props
            if (fieldNames && (key in fieldNames)) {
                key = fieldNames[key as keyof FieldNames] as string
            }
            return !treeData.find((data: TreeDataOption) => data[key] && data[key].length)
        }
        return true;
    }, [props.children, props.treeData, props.loadData, props.fieldNames]);


    const _dropdownClassName = useMemo(() => {
        const {dropdownClassName, fieldid, clsPrefix, popupClassName} = props;
        let extraDropdownClassName = ''
        if (fieldid) {
            extraDropdownClassName = `${clsPrefix}-${fieldid}-dropdown`
        }
        const treeSelectPrefixCls = 'tree-select';
        return classNames(dropdownClassName || popupClassName, `${treeSelectPrefixCls}-dropdown`, `${hasChildren ? 'no-padding-left' : ''}`, `${extraDropdownClassName}`);
    }, [hasChildren, props.dropdownClassName, props.fieldid, props.clsPrefix, props.popupClassName]);

    const getPopupContainerDom = useCallback((dom: HTMLElement) => {
        const {getPopupContainer} = props;
        if (typeof getPopupContainer === 'function') {
            return getPopupContainer(dom)
        } else {
            return cssUtil.parentsUntil(dom);
        }
    }, [props.getPopupContainer]);

    const _switcherIcon = useMemo(() => {
        const {fieldid, switcherIcon} = props;
        if (fieldid) {
            if (switcherIcon) {
                return React.cloneElement(switcherIcon, {
                    fieldid: `${fieldid}_treeselect_switcher`
                })
            } else { // 如果传了fieldid却没有传switcherIcon，生成一个span
                return <span
                    fieldid={`${fieldid}_treeselect_switcher`}
                    style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 1 }}
                />
            }
        } else {
            return switcherIcon || null;
        }
    }, [props.fieldid, props.switcherIcon]);

    const addTreeNodesFieldId = useCallback(() => {
        if (!addTreeNodesFieldIdRef.current) { // 下拉框被显示出来当时候，或者tree展开收起时，处理treenode的fieldid
            addTreeNodesFieldIdRef.current = true;
            setTimeout(() => {
                const { fieldid, clsPrefix } = props;
                // const { valuesArr } = this.state
                const wrapper = document.getElementsByClassName(`${clsPrefix}-${fieldid}-dropdown`)
                if (!wrapper.length) return
                const treeNodes = wrapper[0].querySelectorAll(`.${clsPrefix}-tree-list .${clsPrefix}-tree-treenode`)
                for (let i = 0; i < treeNodes.length; i++) {
                    treeNodes[i].setAttribute('fieldid', `${fieldid}_option_${i}`) // 先和Select一样，用index，如果不合适，再用value
                    // dom[i].setAttribute('fieldid', `${fieldid}_option_${valuesArr[i]}`)
                }
            }, 0);
        }

    }, [props.fieldid, props.clsPrefix]);

    // 处理treenode的fieldid，在组件挂载时和下拉框展开收起的时候处理
    useEffect(() => {
        const { fieldid, clsPrefix } = props;
        if (fieldid) {
            const wrapper = document.getElementsByClassName(`${clsPrefix}-tree-${fieldid}`);
            if (wrapper.length) {
                wrapper[0].setAttribute('fieldid', fieldid);
            }
            if (open) {
                addTreeNodesFieldId();
            }
        }
    }, [props.fieldid, props.clsPrefix, open, addTreeNodesFieldId]);

    const handleTreeExpand = useCallback((expandedKeys: string[]) => {
        if (props.onTreeExpand) {
            props.onTreeExpand(expandedKeys);
        }
        addTreeNodesFieldIdRef.current = false;
        addTreeNodesFieldId();
    }, [props.onTreeExpand, addTreeNodesFieldId]);

    const parentsHaveClass = useCallback((element: HTMLElement, className: string): boolean => {
        const parent = element.parentElement as HTMLElement;
        if (parent.nodeName === 'BODY') {
            return false
        }
        if (parent.classList.contains(className)) {
            return true;
        }
        return parentsHaveClass(parent, className);
    }, []);

    // 仅纵向或者仅横向拖拽
    const getResizeBoxEnable = useCallback((oldEnable: EnableProps, resizable: boolean | 'vertical' | 'horizontal') => {
        let {top = false, left = false, bottom = false, right = false} = oldEnable;
        // 返回两项，其中有一项没传默认为false
        if (resizable === "vertical") {
            return { bottom, top };
        }
        if (resizable === "horizontal") {
            return { left, right };
        }
        return oldEnable;
    }, []);

    const onMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
        const {clsPrefix, getPopupContainer, resizable} = props;
        const resizebox = e.currentTarget as HTMLElement;
        const rootNode = rcTreeSelectRef.current?.getSelectDOMNode() as HTMLElement;
        const {bottom, top, left, right} = resizebox.getBoundingClientRect();
        const boundsRect = typeof getPopupContainer === 'function' && getPopupContainer()?.getBoundingClientRect();
        footerHeightRef.current = footerRef.current?.offsetHeight ? footerRef.current?.offsetHeight : 0;
        let newMinWidth = rootNode.offsetWidth - 2; // 2px 为下拉框的border
        let newMaxHeight = maxHeight;
        let newMaxWidth = maxWidth;
        let newResizeClass = resizeClass;
        let newEnable = enable;
        if (parentsHaveClass(resizebox, `${clsPrefix}-dropdown-placement-bottomRight`)) {
            newMaxWidth = boundsRect ? right - boundsRect.left : right;
            newMaxHeight = boundsRect ? boundsRect.bottom - top : window.innerHeight - top;
            newEnable = { bottom: true, left: true, bottomLeft: true};
            newResizeClass = 'resizable-bottomLeft';
        } else if (parentsHaveClass(resizebox, `${clsPrefix}-dropdown-placement-topRight`)) {
            newMaxWidth = boundsRect ? right - boundsRect.left : right;
            newMaxHeight = boundsRect ? bottom - boundsRect.top : bottom;
            newEnable = { top: true, left: true, topLeft: true};
            newResizeClass = 'resizable-topLeft';
        } else if (parentsHaveClass(resizebox, `${clsPrefix}-dropdown-placement-topLeft`)) {
            newMaxWidth = boundsRect ? boundsRect.right - left : window.innerWidth - left;
            newMaxHeight = boundsRect ? bottom - boundsRect.top : bottom;
            newEnable = { top: true, right: true, topRight: true};
            newResizeClass = 'resizable-topRight';
        } else { // 默认下拉框位置是bottomLeft
            newMaxWidth = boundsRect ? boundsRect.right - left : window.innerWidth - left;
            newMaxHeight = boundsRect ? boundsRect.bottom - top : window.innerHeight - top;
            newEnable = { bottom: true, right: true, bottomRight: true};
            newResizeClass = 'resizable-bottomRight';
        }
        if (resizable === 'vertical' || resizable === "horizontal") {
            newEnable = getResizeBoxEnable(newEnable, resizable);
        }
        newMaxHeight -= 8;
        setMinWidth(newMinWidth);
        setMaxHeight(newMaxHeight);
        setMaxWidth(newMaxWidth);
        setResizeClass(newResizeClass);
        setEnable(newEnable);
    }, [maxHeight, maxWidth, resizeClass, enable, props.clsPrefix, props.getPopupContainer, props.resizable, parentsHaveClass, getResizeBoxEnable]);

    const onResize: ResizeCallback = useCallback((event, direction, elementRef, delta) => {
        const {onResize} = props;
        setInnerListHeight(elementRef.offsetHeight - footerHeightRef.current - 8);
        typeof onResize === "function" && onResize(event, direction, elementRef, delta);
    }, [props.onResize]);

    const onResizeStop: ResizeCallback = useCallback((event, direction, elementRef, delta) => {
        const {onResizeStop} = props;
        setInnerListHeight(elementRef.offsetHeight - footerHeightRef.current - 8);
        typeof onResizeStop === "function" && onResizeStop(event, direction, elementRef, delta);
    }, [props.onResizeStop]);

    const resizableDropdown = useCallback((node: React.ReactElement) => {
        const {dropdownRender, clsPrefix, listHeight} = props;
        const Footer = ({ children, height, style, ...otherProps }: any) => {
            footerHeightRef.current = height ? height : footerHeightRef.current;
            return <footer
                {...otherProps}
                style={{position: children && footerHeightRef.current ? 'absolute' : 'relative', height, ...style}}
                ref={ref => footerRef.current = ref}
                className={`${clsPrefix}-tree-resizebox-footer`}
            >
                <Divider style={{ margin: '8px 0' }}/>
                {children as React.ReactElement}
            </footer>
        };
        return <Resizable
            className={classNames([`${clsPrefix}-tree-resizebox`], resizeClass)}
            style={{ paddingBottom: footerHeightRef.current ? `${footerHeightRef.current}px` : 0 }}
            minHeight={listHeight || 200}
            minWidth={minWidth}
            maxHeight={maxHeight}
            maxWidth={maxWidth}
            enable={enable}
            onResize={onResize}
            onResizeStart={props.onResizeStart}
            onResizeStop={onResizeStop}
            onMouseEnter={onMouseEnter}
            handleWrapperClass={'resize-handle'}
            handleClasses={{
                top: `${clsPrefix}-resizebox-handle-top`,
                right: `${clsPrefix}-resizebox-handle-right`,
                bottom: `${clsPrefix}-resizebox-handle-bottom`,
                left: `${clsPrefix}-resizebox-handle-left`,
            }}
        >
            {typeof dropdownRender === "function" ? dropdownRender(node, Footer) : node}
        </Resizable>;
    }, [props.dropdownRender, props.clsPrefix, props.listHeight, props.onResizeStart, minWidth, maxHeight, maxWidth, enable, resizeClass, onResizeStop, onMouseEnter, onResize]);

    const handleDropdownVisibleChange = useCallback((open: boolean) => {
        const {onDropdownVisibleChange} = props;
        if (!('open' in props) || ('open' in props && typeof props.open !== "boolean")) {
            setOpen(open);
        }
        onDropdownVisibleChange && onDropdownVisibleChange(open);
    }, [props.onDropdownVisibleChange]);

    const {
        clsPrefix,
        className,
        size = defaultProps.size,
        notFoundContent,
        dropdownStyle,
        suffixIcon,
        switcherIcon,
        requiredStyle,
        align,
        bordered = true,
        fieldid = defaultProps.fieldid,
        onTreeExpand,
        onDropdownVisibleChange,
        resizable,
        dropdownRender,
        dropdownMatchSelectWidth,
        listHeight,
        showCheckedStrategy = defaultProps.showCheckedStrategy,
        popupClassName,
        locale = defaultProps.locale,
        showSearch = defaultProps.showSearch,
        showArrow = defaultProps.showArrow,
        readOnly = defaultProps.readOnly,
        ...restProps
    } = props;
    isShouldUpdate("TreeSelect", props);
    const fieldidClassName = fieldid ? `${clsPrefix}-tree-${fieldid}` : '';
    const local = getLangInfo(locale, i18n);

    const cls = classNames(
        `${clsPrefix}-tree-select`,
        size && `${clsPrefix}-${setComponentSize(size, {defaultIsMd: true})}`,
        setComponentClass({clsPrefix, bordered, align, requiredStyle, readOnly}),
        className,
        fieldidClassName
    );
    // showSearch: single - false, multiple - true
    let finalShowSearch = showSearch;
    if (!('showSearch' in props)) {
        finalShowSearch = !!(restProps.multiple || restProps.treeCheckable);
    }
    let mergedNotFound = notFoundContent;
    if (notFoundContent === undefined) { // 优先显示传了notFoundContent的内容
        mergedNotFound = local.langMap.notFoundContent;
    }
    let checkable = restProps.treeCheckable;
    if (checkable) {
        checkable = <span className={`${clsPrefix}-tree-checkbox-inner`}/>;
    }

    let inputIcon = showArrow ? ((suffixIcon &&
		(React.isValidElement(suffixIcon)
		    ? React.cloneElement(suffixIcon)
		    : suffixIcon)) || <Icon type="uf-arrow-down" className={`${clsPrefix}-arrow-icon`}/>) : null;

    if (fieldid && inputIcon) {
        inputIcon = React.cloneElement(inputIcon as React.ReactElement, {
            fieldid: `${fieldid}_suffix_icon`
        })
    }
    const removeIcon = <Icon type="uf-close" className={`${clsPrefix}-remove-icon`}/>;

    const clearIcon = (
        <Icon type="uf-close-c"/>
    );
    const switcherIconProp = {
        switcherIcon: _switcherIcon
    };
    const onTreeExpandProp = fieldid ?
        { onTreeExpand: handleTreeExpand } :
        { onTreeExpand };
    const fieldidProp = fieldid ? { fieldid } : {}
    let adapterNid = getNid(props);
    return (
        <RcTreeSelect
            inputIcon={inputIcon}
            showArrow={showArrow}
            showCheckedStrategy={showCheckedStrategy}
            removeIcon={removeIcon}
            clearIcon={clearIcon}
            readOnly={readOnly}
            {...restProps}
            {...switcherIconProp}
            showSearch={finalShowSearch}
            getPopupContainer={getPopupContainerDom}
            dropdownClassName={_dropdownClassName}
            prefixCls={clsPrefix}
            className={cls}
            dropdownStyle={resizable ? dropdownStyle : {maxHeight: '100vh', overflow: 'auto', ...dropdownStyle}}
            dropdownRender={resizable ? resizableDropdown : dropdownRender}
            dropdownMatchSelectWidth={resizable ? false : dropdownMatchSelectWidth}
            listHeight={resizable ? innerListHeight : listHeight}
            listItemHeight={28}
            notFoundContent={mergedNotFound}
            treeCheckable={checkable}
            ref={saveTreeSelect}
            onDropdownVisibleChange={handleDropdownVisibleChange}
            open={open}
            {...onTreeExpandProp}
            {...fieldidProp}
            {...adapterNid}
        />
    );
};

const TreeSelectComponent = WithConfigConsumer({name: "treeSelect"})(
    WebUI({name: "select", defaultProps})(TreeSelect)
);

TreeSelectComponent.TreeNode = TreeNode;
(TreeSelectComponent as any).SHOW_ALL = SHOW_ALL;
(TreeSelectComponent as any).SHOW_PARENT = SHOW_PARENT;
(TreeSelectComponent as any).SHOW_CHILD = SHOW_CHILD;

export default TreeSelectComponent;